/**
 * Chunked uploader — slices a single file into ≤ 5 MB chunks and pushes
 * them through the backend's chunk/init → chunk/upload* → chunk/finalize
 * pipeline. Required for any file larger than the Kubernetes ingress's
 * ~10 MB request cap (pro videos, RAW exports).
 *
 * Resumability:
 *   • Per-chunk retry with exponential backoff (3 attempts) absorbs
 *     transient blips without bothering the user.
 *   • On every successful init, the {sessionId, totalChunks} is stashed
 *     in localStorage keyed by `${galleryId}::${name}::${size}::
 *     ${lastModified}`. On the next attempt to upload the same file,
 *     we reuse the cached session and skip chunks the server already
 *     received (queried via /chunk/status).
 *   • This means: lose Wi-Fi mid-upload of a 4K event reel → reconnect
 *     → re-trigger the upload of the same file → resumes from the last
 *     successful chunk instead of starting over.
 *
 * Usage:
 *   const media = await chunkedUpload({
 *     api, galleryId, file,
 *     onProgress: (pct, info) => setProgress(pct),
 *     onStatus: (label) => setStatusText(label),  // optional
 *   });
 */

// 5 MB chunks keep us well below the 10 MB ingress wall even after
// multipart envelope overhead. Backend's hard cap is 8 MB per chunk.
const CHUNK_SIZE = 5 * 1024 * 1024;

// Resumability tuning — 3 retries × exponential backoff (1s, 2s, 4s)
// burns ~7s total before a transient blip surfaces as a real error.
// Long enough for a Wi-Fi roam, short enough to fail fast on a dead
// uplink.
const MAX_CHUNK_RETRIES = 3;
const RETRY_BASE_MS = 1000;

// localStorage key namespace.
const LS_KEY = "aristocrats.chunkedUploadSessions";

function _fingerprint(galleryId, file) {
  // Stable identity for the same (gallery, file) pair across reloads.
  // size + lastModified is enough — a user re-picking a different file
  // with the same name will still produce a different fingerprint.
  return `${galleryId}::${file.name}::${file.size}::${file.lastModified}`;
}

function _readSessions() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
  catch { return {}; }
}

function _writeSessions(map) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); }
  catch { /* quota / private mode — degrade silently to no-resume */ }
}

function _saveCachedSession(galleryId, file, sessionId, totalChunks) {
  const map = _readSessions();
  map[_fingerprint(galleryId, file)] = {
    sessionId, totalChunks, createdAt: Date.now(),
  };
  _writeSessions(map);
}

function _loadCachedSession(galleryId, file) {
  const map = _readSessions();
  const entry = map[_fingerprint(galleryId, file)];
  if (!entry) return null;
  // Expire cached sessions older than the backend's 1-hour TTL so we
  // don't try to resume a session the server has already swept.
  if (Date.now() - entry.createdAt > 60 * 60 * 1000) {
    delete map[_fingerprint(galleryId, file)];
    _writeSessions(map);
    return null;
  }
  return entry;
}

function _clearCachedSession(galleryId, file) {
  const map = _readSessions();
  delete map[_fingerprint(galleryId, file)];
  _writeSessions(map);
}

/**
 * Run `fn` with retry + exponential backoff. Only retries on transient
 * failures (network drop, timeout, 5xx). 4xx errors surface immediately
 * because they're permanent (bad auth, bad input, gallery deleted).
 */
async function _withRetry(fn, label) {
  let lastErr;
  for (let attempt = 0; attempt <= MAX_CHUNK_RETRIES; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const transient =
        !status // network failure, no response
        || status >= 500
        || status === 408
        || status === 429
        || err?.code === "ECONNABORTED"
        || err?.code === "ERR_NETWORK"
        || err?.code === "ETIMEDOUT";
      if (!transient || attempt === MAX_CHUNK_RETRIES) break;
      const delay = RETRY_BASE_MS * Math.pow(2, attempt);
      // eslint-disable-next-line no-console
      console.warn(`[chunkedUpload] ${label} attempt ${attempt + 1} failed (${status || err?.code}). Retrying in ${delay}ms.`);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

export async function chunkedUpload({ api, galleryId, file, onProgress, onStatus }) {
  const totalSize = file.size;
  const totalChunks = Math.max(1, Math.ceil(totalSize / CHUNK_SIZE));

  // ---- 1. Try to resume an existing session for this exact file ----
  let sessionId = null;
  let alreadyDone = new Set();
  const cached = _loadCachedSession(galleryId, file);
  if (cached && cached.totalChunks === totalChunks) {
    try {
      const statusRes = await api.get(
        `/admin/galleries/${galleryId}/chunk/status`,
        { params: { session_id: cached.sessionId }, timeout: 10000 },
      );
      // Only resume if the server still has the session AND it's in a
      // recoverable state (uploading or already-finalized are both OK).
      const srvStatus = statusRes.data.status;
      if (srvStatus === "uploading" || srvStatus === "finalizing") {
        sessionId = cached.sessionId;
        alreadyDone = new Set(statusRes.data.received_chunks || []);
        if (alreadyDone.size > 0 && onStatus) {
          onStatus(`Resuming — ${alreadyDone.size}/${totalChunks} chunks already uploaded`);
        }
      } else if (srvStatus === "finalized") {
        // Edge case: previous attempt finalized but localStorage wasn't
        // cleared (e.g. tab killed before finalize response landed).
        // Re-finalize is idempotent — we'll catch it in the finalize call.
        sessionId = cached.sessionId;
        alreadyDone = new Set(Array.from({ length: totalChunks }, (_, i) => i));
      }
    } catch {
      // Server forgot the session (TTL'd, aborted, or never existed) — fall
      // through to a fresh init. Clear stale cache so we don't try again.
      _clearCachedSession(galleryId, file);
    }
  }

  // ---- 2. No resumable session — init a fresh one ----
  if (!sessionId) {
    const initRes = await _withRetry(
      () => api.post(`/admin/galleries/${galleryId}/chunk/init`, {
        filename: file.name,
        total_size: totalSize,
        total_chunks: totalChunks,
        content_type: file.type || "application/octet-stream",
      }, { timeout: 15000 }),
      "chunk/init",
    );
    sessionId = initRes.data.session_id;
    _saveCachedSession(galleryId, file, sessionId, totalChunks);
  }

  try {
    // Emit baseline progress so the bar reflects already-done chunks
    // immediately on resume (instead of jumping from 0 to N/total).
    if (alreadyDone.size > 0 && onProgress) {
      const basePct = Math.round((alreadyDone.size / totalChunks) * 100);
      onProgress(basePct, {
        chunk: alreadyDone.size,
        totalChunks,
        bytes: Math.min(alreadyDone.size * CHUNK_SIZE, totalSize),
        totalBytes: totalSize,
        filename: file.name,
        resumed: true,
      });
    }

    // ---- 3. Upload remaining chunks sequentially with retry ----
    for (let i = 0; i < totalChunks; i++) {
      if (alreadyDone.has(i)) continue; // already on server

      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalSize);
      const chunk = file.slice(start, end);

      await _withRetry(() => {
        const fd = new FormData();
        fd.append("session_id", sessionId);
        fd.append("chunk_index", String(i));
        fd.append("chunk", chunk, `${file.name}.part${i}`);
        return api.post(`/admin/galleries/${galleryId}/chunk/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
      }, `chunk/upload[${i}]`);

      const pct = Math.round(((i + 1) / totalChunks) * 100);
      if (onProgress) {
        onProgress(pct, {
          chunk: i + 1,
          totalChunks,
          bytes: end,
          totalBytes: totalSize,
          filename: file.name,
        });
      }
    }

    // ---- 4. Finalize — backend reassembles, runs gallery pipeline ----
    const finRes = await _withRetry(
      () => api.post(`/admin/galleries/${galleryId}/chunk/finalize`,
        { session_id: sessionId },
        { timeout: 180000 }),
      "chunk/finalize",
    );
    _clearCachedSession(galleryId, file);
    return finRes.data;
  } catch (err) {
    // Distinguish permanent failures from network-transient ones. On a
    // transient failure we leave the cached session in localStorage so
    // the user can retry and resume. On a permanent failure (4xx) we
    // abort + clear cache so a stale session doesn't haunt them.
    const status = err?.response?.status;
    const isPermanent = status && status >= 400 && status < 500
      && status !== 408 && status !== 429;
    if (isPermanent) {
      try {
        await api.post(`/admin/galleries/${galleryId}/chunk/abort`,
          { session_id: sessionId });
      } catch { /* ignored */ }
      _clearCachedSession(galleryId, file);
    } else if (onStatus) {
      onStatus("Connection lost — re-pick the same file to resume from this point.");
    }
    throw err;
  }
}

/**
 * Convenience: which path should a file take?
 *  • Small images → existing batch `/multi-upload` (one round trip).
 *  • Files > 4 MB OR any video → chunked pipeline.
 */
const CHUNKED_THRESHOLD_BYTES = 4 * 1024 * 1024;
const VIDEO_TYPE_PREFIX = "video/";

export function shouldChunk(file) {
  if (!file) return false;
  if (file.size > CHUNKED_THRESHOLD_BYTES) return true;
  if (file.type && file.type.startsWith(VIDEO_TYPE_PREFIX)) return true;
  return false;
}

export const CHUNKED_UPLOAD_CHUNK_SIZE = CHUNK_SIZE;

/**
 * For UI flows that want to surface "you have N partial upload(s)
 * waiting" — returns the list of cached sessions for a given gallery.
 * Each entry is { fingerprint, sessionId, totalChunks, createdAt }.
 */
export function listPartialUploads(galleryId) {
  const map = _readSessions();
  return Object.entries(map)
    .filter(([k]) => k.startsWith(`${galleryId}::`))
    .map(([k, v]) => ({ fingerprint: k, ...v }));
}
